import { type Dispatch, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/atoms/Card';
import FormField from '@/components/atoms/FormField';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import TextArea from '@/components/atoms/TextArea';
import TextField from '@/components/atoms/TextField';

import type { OperateWizardAction, OperateWizardState } from '../../operateWizardReducer';

import styles from '../../operateModals.module.scss';

interface Props {
    state: OperateWizardState;
    operationMinDate: Date | null;
    dispatch: Dispatch<OperateWizardAction>;
}

/** The operation's date, reason and notes. */
export function ConfirmMetadataFields(props: Props) {
    const { state, operationMinDate, dispatch } = props;
    const { t } = useTranslation('fishGroups');
    const operationDateInputId = useId();
    const reasonInputId = useId();
    const notesInputId = useId();

    return (
        <Card as="article" radius="sm" padding="xs" className={styles.confirmSection}>
            <h5 className={styles.confirmSectionTitle}>
                {t('actions.operate.confirm.metadataSection')}
            </h5>
            <div className={styles.fieldsGrid}>
                {/*
                  Required, unlike the reason/notes below it: the operation date is what makes
                  a split or merge traceable afterwards. Defaults to today, so the operator only
                  sees the error if they clear it.
                */}
                <FormField
                    size="sm"
                    htmlFor={operationDateInputId}
                    label={t('actions.operate.confirm.fields.operationDate')}
                    error={state.operationDate === null ? t('actions.errors.required') : undefined}>
                    <StyledSingleDatePicker
                        id={operationDateInputId}
                        date={state.operationDate ? new Date(state.operationDate) : null}
                        onDateChange={(date) =>
                            dispatch({
                                type: 'setOperationDate',
                                value: date ? date.toISOString() : null,
                            })
                        }
                        blockFutureDates
                        minDate={operationMinDate}
                    />
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={reasonInputId}
                    label={t('actions.operate.confirm.fields.reason')}
                    labelSuffix={t('actions.fields.optional')}>
                    <TextField
                        id={reasonInputId}
                        type="text"
                        value={state.reason}
                        onChange={(e) => dispatch({ type: 'setReason', value: e.target.value })}
                    />
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={notesInputId}
                    label={t('actions.operate.confirm.fields.notes')}
                    labelSuffix={t('actions.fields.optional')}
                    className={styles['field--fullWidth']}>
                    <TextArea
                        id={notesInputId}
                        rows={3}
                        value={state.notes}
                        onChange={(e) => dispatch({ type: 'setNotes', value: e.target.value })}
                    />
                </FormField>
            </div>
        </Card>
    );
}
